import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventDate: "",
    eventTime: "",
    guestCount: "",
    eventType: "",
    dietaryRestrictions: "",
    kitchenDetails: "",
    budgetRange: "",
    additionalMessage: "",
    newsLetter: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStarClick = (rating) => {
    setReviewRating(rating);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingReview(true);
    
    // Simulate review submission
    setTimeout(() => {
      setIsSubmittingReview(false);
      // Reset review form
      setReviewRating(0);
      setReviewText("");
      setReviewerName("");
    }, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          eventDate: "",
          eventTime: "",
          guestCount: "",
          eventType: "",
          dietaryRestrictions: "",
          kitchenDetails: "",
          budgetRange: "",
          additionalMessage: "",
          newsLetter: false
        });
        setSubmitStatus(null);
      }, 3000);
    }, 2000);
  };

  const eventTypes = [
    "Private Dinner Party",
    "Anniversary/Birthday Celebration",
    "Corporate Event",
    "Holiday Party",
    "Cooking Class",
    "Meal Prep Service",
    "Wine Pairing Dinner",
    "Other"
  ];

  const budgetRanges = [
    "Under $500",
    "$500 - $1,000",
    "$1,000 - $2,000",
    "$2,000 - $3,500",
    "$3,500 - $5,000",
    "$5,000+",
    "Prefer to discuss"
  ];

  const kitchenTypes = [
    "Full kitchen with gas stove",
    "Full kitchen with electric stove",
    "Limited kitchen facilities",
    "Outdoor kitchen/grill setup",
    "Need to discuss kitchen details"
  ];

  return (
    <div className="page-contact">
      {/* Hero Section */}
      <section className="hero-section bg-primary py-16">
        <div className="container text-center">
          <h1 className="hero-large mb-6">Let's Create Something Amazing</h1>
          <p className="body-large text-secondary max-w-2xl mx-auto">
            Ready to plan your perfect culinary experience? Fill out the form below 
            and I'll get back to you within 24 hours with a custom proposal.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="heading-2 mb-6">Get In Touch</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <Mail className="w-5 h-5 text-interactive-base mt-1" />
                    <div>
                      <p className="body-regular font-medium">Email</p>
                      <p className="body-small text-secondary">zacharyskalmbach@gmail.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Phone className="w-5 h-5 text-interactive-base mt-1" />
                    <div>
                      <p className="body-regular font-medium">Phone</p>
                      <p className="body-small text-secondary">(561) 873-7499</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-5 h-5 text-interactive-base mt-1" />
                    <div>
                      <p className="body-regular font-medium">Service Area</p>
                      <p className="body-small text-secondary">South East Florida: Broward, Miami-Dade, Palm Beach County</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Clock className="w-5 h-5 text-interactive-base mt-1" />
                    <div>
                      <p className="body-regular font-medium">Response Time</p>
                      <p className="body-small text-secondary">Within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <Card className="bg-accent border-none">
                <CardHeader>
                  <CardTitle className="heading-3">What to Expect</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-interactive-base rounded-full mt-2"></div>
                    <p className="body-small">Personalized consultation call</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-interactive-base rounded-full mt-2"></div>
                    <p className="body-small">Custom menu proposal</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-interactive-base rounded-full mt-2"></div>
                    <p className="body-small">Detailed pricing breakdown</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-interactive-base rounded-full mt-2"></div>
                    <p className="body-small">Event timeline and logistics</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-border-medium bg-secondary shadow-lg">
                <CardHeader className="bg-subtle/50 border-b border-border-light">
                  <CardTitle className="heading-2 text-interactive-base">Event Planning Form</CardTitle>
                  <CardDescription className="body-regular text-primary mt-2">
                    Please provide as much detail as possible so I can create the perfect culinary experience for you.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="body-small text-green-800">
                        Thank you! Your inquiry has been submitted successfully. I'll be in touch within 24 hours.
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    {/* Event Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="eventDate">Preferred Event Date *</Label>
                        <Input
                          id="eventDate"
                          name="eventDate"
                          type="date"
                          required
                          value={formData.eventDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventTime">Preferred Start Time *</Label>
                        <Input
                          id="eventTime"
                          name="eventTime"
                          type="time"
                          required
                          value={formData.eventTime}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="guestCount">Number of Guests *</Label>
                        <Input
                          id="guestCount"
                          name="guestCount"
                          type="number"
                          min="1"
                          max="50"
                          required
                          value={formData.guestCount}
                          onChange={handleInputChange}
                          placeholder="6"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventType">Event Type *</Label>
                        <Select onValueChange={(value) => handleSelectChange('eventType', value)} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select event type" />
                          </SelectTrigger>
                          <SelectContent>
                            {eventTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Special Requirements */}
                    <div className="space-y-2">
                      <Label htmlFor="dietaryRestrictions">Dietary Restrictions/Allergies</Label>
                      <Textarea
                        id="dietaryRestrictions"
                        name="dietaryRestrictions"
                        value={formData.dietaryRestrictions}
                        onChange={handleInputChange}
                        placeholder="Please list any allergies, dietary restrictions, or food preferences..."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kitchenDetails">Kitchen Setup *</Label>
                      <Select onValueChange={(value) => handleSelectChange('kitchenDetails', value)} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Describe your kitchen setup" />
                        </SelectTrigger>
                        <SelectContent>
                          {kitchenTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="budgetRange">Budget Range *</Label>
                      <Select onValueChange={(value) => handleSelectChange('budgetRange', value)} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetRanges.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additionalMessage">Additional Details</Label>
                      <Textarea
                        id="additionalMessage"
                        name="additionalMessage"
                        value={formData.additionalMessage}
                        onChange={handleInputChange}
                        placeholder="Tell me more about your vision, special requests, or any other details that would help me create the perfect experience for you..."
                        rows={4}
                      />
                    </div>

                    {/* Checkbox */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="newsletter"
                        name="newsletter"
                        checked={formData.newsLetter}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, newsLetter: checked }))}
                      />
                      <Label htmlFor="newsletter" className="body-small">
                        I'd like to receive updates about seasonal menus and special offers
                      </Label>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-interactive-base hover:bg-interactive-hover text-white py-3 h-auto"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          <span>Sending...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Send className="w-4 h-4" />
                          <span>Send Inquiry</span>
                        </div>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Client Reviews Section */}
      <section className="section-padding bg-subtle">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Share Your Experience</h2>
            <p className="body-large text-secondary max-w-2xl mx-auto">
              Your feedback helps me continue to create exceptional dining experiences. 
              Please share your thoughts about your event.
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <Card className="border-border-medium bg-secondary shadow-lg">
              <CardHeader className="bg-subtle/50 border-b border-border-light">
                <CardTitle className="heading-2 text-interactive-base">Leave a Review</CardTitle>
                <CardDescription className="body-regular text-primary mt-2">
                  Tell others about your culinary experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleReviewSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reviewerName">Your Name *</Label>
                      <Input
                        id="reviewerName"
                        name="reviewerName"
                        type="text"
                        required
                        value={reviewerName}
                        onChange={(e) => setReviewerName(e.target.value)}
                        placeholder="Full name or business name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="eventType">Event Type</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="private-dinner">Private Dinner</SelectItem>
                          <SelectItem value="cooking-class">Cooking Class</SelectItem>
                          <SelectItem value="meal-prep">Meal Prep Service</SelectItem>
                          <SelectItem value="large-gathering">Large Gathering/Event</SelectItem>
                          <SelectItem value="yacht-charter">Yacht Charter Experience</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Rating *</Label>
                    <div className="flex items-center justify-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className="p-1 hover:scale-110 transition-transform"
                          onClick={() => handleStarClick(star)}
                          onMouseEnter={() => setHoveredStar(star)}
                          onMouseLeave={() => setHoveredStar(0)}
                        >
                          <Star 
                            className={`w-8 h-8 transition-colors ${
                              star <= (hoveredStar || reviewRating) 
                                ? 'fill-current text-interactive-base' 
                                : 'text-interactive-base stroke-current fill-transparent'
                            }`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reviewText">Your Review *</Label>
                    <Textarea
                      id="reviewText"
                      name="reviewText"
                      required
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share details about your experience, the food, service, and overall impression..."
                      rows={6}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox id="allowPublic" />
                    <Label htmlFor="allowPublic" className="body-small">
                      I allow this review to be displayed publicly on the website
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmittingReview || reviewRating === 0 || !reviewText.trim() || !reviewerName.trim()}
                    className="w-full bg-interactive-base hover:bg-interactive-hover text-bg-primary font-semibold py-4 h-auto border-0 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingReview ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin w-5 h-5 border-2 border-bg-primary border-t-transparent rounded-full"></div>
                        <span>Submitting Review...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Star className="w-5 h-5" />
                        <span>Submit My Review</span>
                      </div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;